import type { ReactNode } from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  List,
  Stack,
  Typography,
} from "@mui/material";
import { userApi } from "@bw/shared/api";
import { playerHandleSuffix } from "@bw/shared/components";
import { useAuth } from "@bw/shared/hooks";
import type {
  HeadToHeadResponse,
  PlayerProfileStatsResponse,
  PlayerProfileSummaryResponse,
  PlayerRatingResponse,
  UserProfileResponse,
} from "@bw/shared/types/generated";
import { useQuery } from "@tanstack/react-query";
import { hasSafeAvatar, initials } from "../../../common/avatar";
import { DenseRow } from "../../../common/components/DenseRow";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { usePlayerProfile } from "../hooks/usePlayerProfile";
import { useProfileSummary } from "../hooks/useProfileSummary";
import { useRating } from "../hooks/useRating";
import { MatchList } from "./MatchList";
import { RatingCard } from "./RatingCard";
import { standingLabel, winRateLabel } from "./competeDisplay";
import { PLAY_FORMAT_LABELS, SKILL_LEVEL_LABELS } from "../../../common/components/PlayStyleFields";

export interface PlayerProfileViewProps {
  userId: string;
  /** Relationship actions (Add friend / Message, or Edit / Share for oneself). */
  actions?: ReactNode;
  /** The viewer's head-to-head with this player, when it is someone else. */
  headToHead?: HeadToHeadResponse;
  /** Posts, rendered in the main column. */
  posts?: ReactNode;
}

export function PlayerProfileView(props: PlayerProfileViewProps) {
  const { user } = useAuth();
  const identity = useQuery({
    queryKey: ["performance", "identity", user?.uid, props.userId],
    queryFn: () => userApi.getProfile(props.userId),
    enabled: Boolean(user && props.userId),
    retry: false,
  });
  if (!user || identity.isLoading)
    return <Typography role="status">Loading profile…</Typography>;
  if (identity.isError || !identity.data)
    return (
      <Alert severity="info">
        This profile isn&apos;t available. The player may keep it private.
      </Alert>
    );
  return <AuthorizedProfileView {...props} identity={identity.data} />;
}

/**
 * How they play: the events they chose (else the ones they've played), then
 * their stated level.
 */
function howTheyPlay(identity: UserProfileResponse, stats: PlayerProfileStatsResponse | undefined): string[] {
  const chosen = identity.play_formats ?? [];
  const formats = chosen.length ? chosen.map((format) => PLAY_FORMAT_LABELS[format]) : playedFormats(stats);
  return identity.skill_level ? [...formats, SKILL_LEVEL_LABELS[identity.skill_level]] : formats;
}

/** Formats the player has actually played, in display order. */
function playedFormats(stats: PlayerProfileStatsResponse | undefined): string[] {
  if (!stats) return [];
  return [
    stats.singles_played > 0 ? "Singles" : null,
    stats.doubles_played > 0 ? "Doubles" : null,
    (stats.mixed_played ?? 0) > 0 ? "Mixed" : null,
  ].filter((value): value is string => Boolean(value));
}

function AuthorizedProfileView({
  userId,
  identity,
  actions,
  headToHead,
  posts,
}: PlayerProfileViewProps & { identity: UserProfileResponse }) {
  const { user } = useAuth();
  const isSelf = user?.uid === userId;
  const ratingQ = useRating(userId);
  const statsQ = usePlayerProfile(userId);
  const summaryQ = useProfileSummary(userId);
  const rating = ratingQ.data;
  const summary = summaryQ.data;
  const placed = rating?.is_placed === true;
  // Competition appears once there are counted matches (placement included).
  const hasMatches =
    (statsQ.data?.games_played ?? 0) > 0 || (statsQ.data?.recent_matches?.length ?? 0) > 0;
  const displayName = identity.display_name?.trim() || "—";
  // The tier joins the header once placed; before that the Unrated tag says it.
  // Only what other players actually see (the owner's view says so).
  const facts = [
    summary?.shows_play_details ? summary.home_area_label : null,
    rating && placed ? standingLabel(rating) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const header = (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", minWidth: 0 }}>
        <Avatar
          aria-hidden
          src={hasSafeAvatar(identity.avatar) ? (identity.avatar ?? undefined) : undefined}
          sx={{ width: 64, height: 64, fontSize: 22, fontWeight: 700, bgcolor: "primary.main", color: "primary.contrastText" }}
        >
          {displayName === "—" ? null : initials(displayName)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: "1.25rem", fontWeight: 700, overflowWrap: "anywhere" }}>
            {displayName}
            {displayName === "—" ? null : (
              <>
                {" "}
                <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {playerHandleSuffix(userId)}
                </Typography>
              </>
            )}
          </Typography>
          {facts ? (
            <Typography variant="body2" color="text.secondary">
              {facts}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {isSelf ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 0.75, borderRadius: 2, bgcolor: "action.hover" }}>
          This is how other players see your profile.
        </Typography>
      ) : null}
      {identity.bio ? <Typography sx={{ overflowWrap: "anywhere" }}>{identity.bio}</Typography> : null}
      <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }} aria-label="How they play">
        {howTheyPlay(identity, statsQ.data).map((format) => (
          <Chip key={format} label={format} size="small" variant="outlined" />
        ))}
        {rating && !placed ? <Chip label="Unrated" size="small" sx={{ color: "text.secondary" }} /> : null}
      </Stack>
      {actions}
    </Stack>
  );

  const rail = <ProfileCircles summary={summary} isSelf={isSelf} />;

  return (
    <Box
      sx={{
        display: "grid",
        columnGap: 3,
        rowGap: 2.5,
        gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) 280px" },
        // Phone: one column with the circles before posts; desktop: a rail.
        gridTemplateAreas: {
          xs: '"main" "rail" "posts"',
          md: '"main rail" "posts rail"',
        },
        gridTemplateRows: { md: "auto 1fr" },
        alignItems: "start",
      }}
    >
      <Stack spacing={2.5} sx={{ minWidth: 0, gridArea: "main" }}>
        {header}
        {ratingQ.isError ? (
          <Alert severity="error" role="alert">
            Couldn&apos;t load this player&apos;s rating. Please try again.
          </Alert>
        ) : rating && (placed || hasMatches) ? (
          <CompetitionSection
            rating={rating}
            stats={statsQ.data}
            statsUnavailable={statsQ.isError}
            userId={userId}
            viewerId={user?.uid ?? null}
            headToHead={isSelf ? undefined : headToHead}
          />
        ) : null}
      </Stack>
      <Box sx={{ gridArea: "rail", minWidth: 0 }}>{rail}</Box>
      <Box sx={{ gridArea: "posts", minWidth: 0 }}>{posts}</Box>
    </Box>
  );
}

function CompetitionSection({
  rating,
  stats,
  statsUnavailable,
  userId,
  viewerId,
  headToHead,
}: {
  rating: PlayerRatingResponse;
  stats: PlayerProfileStatsResponse | undefined;
  statsUnavailable: boolean;
  userId: string;
  viewerId: string | null;
  headToHead?: HeadToHeadResponse;
}) {
  const formats = stats
    ? [
        { label: "Singles", played: stats.singles_played, rate: stats.singles_win_rate },
        { label: "Doubles", played: stats.doubles_played, rate: stats.doubles_win_rate },
        { label: "Mixed", played: stats.mixed_played ?? 0, rate: stats.mixed_win_rate ?? 0 },
      ]
    : [];
  const headToHeadLine = !headToHead
    ? null
    : headToHead.wins_a === 0 && headToHead.wins_b === 0 && headToHead.draws === 0
      ? "You haven't played each other yet."
      : `Head-to-head with you ${headToHead.wins_a}–${headToHead.wins_b}${
          headToHead.draws > 0 ? `, ${headToHead.draws} ${headToHead.draws === 1 ? "draw" : "draws"}` : ""
        }`;
  return (
    <Stack component="section" aria-labelledby="competition-heading" spacing={1}>
      <SectionHeader id="competition-heading" title="Competition" />
      <RatingCard rating={rating} showName={false} bare />
      {headToHeadLine ? (
        <Typography variant="body2" color="text.secondary" data-testid="head-to-head">
          {headToHeadLine}
        </Typography>
      ) : null}
      {statsUnavailable ? (
        <Alert severity="error" role="alert">
          Stats unavailable right now.
        </Alert>
      ) : stats ? (
        <>
          <Box
            role="list"
            aria-label="Record by format"
            sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1 }}
          >
            {formats.map((format) => (
              <Box
                key={format.label}
                role="listitem"
                sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 1, textAlign: "center" }}
              >
                <Typography variant="caption" color="text.secondary">
                  {format.label}
                </Typography>
                <Typography sx={{ fontWeight: 800 }}>{format.played}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {format.played > 0 ? `${winRateLabel(format.rate, format.played)} won` : "—"}
                </Typography>
              </Box>
            ))}
          </Box>
          {stats.recent_matches?.length ? (
            <MatchList
              matches={stats.recent_matches.slice(0, 3)}
              viewerId={viewerId}
              perspectiveId={userId}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No ranked matches yet.
            </Typography>
          )}
        </>
      ) : null}
    </Stack>
  );
}

function ProfileCircles({
  summary,
  isSelf,
}: {
  summary: PlayerProfileSummaryResponse | undefined;
  isSelf: boolean;
}) {
  if (!summary) return null;
  const places = summary.plays_at ?? [];
  const communities = summary.communities ?? [];
  const mutual = summary.mutual_friend_count ?? 0;
  const ownHint =
    isSelf && !summary.shows_play_details
      ? "Only you can see where you play and your communities. Choose to show them in Edit profile."
      : null;
  if (!places.length && !communities.length && !mutual && !ownHint) return null;
  return (
    <Stack spacing={2}>
      {ownHint ? (
        <Typography variant="body2" color="text.secondary">
          {ownHint}
        </Typography>
      ) : null}
      {places.length ? (
        <Stack component="section" aria-labelledby="plays-at-heading" spacing={0.25}>
          <SectionHeader id="plays-at-heading" title="Plays at" />
          <List disablePadding>
            {places.map((place) => (
              <DenseRow
                key={place.facility_id}
                to={`/facility/${place.facility_id}`}
                leading={<Avatar variant="rounded" aria-hidden sx={{ width: 40, height: 40, fontSize: 14, fontWeight: 700, borderRadius: 2 }}>{initials(place.name)}</Avatar>}
                title={place.name}
                subtitle={place.city ?? ""}
              />
            ))}
          </List>
        </Stack>
      ) : null}
      {communities.length ? (
        <Stack component="section" aria-labelledby="communities-heading" spacing={0.25}>
          <SectionHeader id="communities-heading" title="Communities" />
          <List disablePadding>
            {communities.map((community) => (
              <DenseRow
                key={community.community_id}
                to={`/community/communities/${community.community_id}`}
                leading={<Avatar aria-hidden sx={{ width: 40, height: 40, fontSize: 14, fontWeight: 700 }}>{initials(community.name)}</Avatar>}
                title={community.name}
                subtitle={`${community.member_count} ${community.member_count === 1 ? "member" : "members"}`}
              />
            ))}
          </List>
        </Stack>
      ) : null}
      {mutual ? (
        <Typography variant="body2" color="text.secondary">
          {mutual === 1 ? "1 mutual friend" : `${mutual} mutual friends`}
        </Typography>
      ) : null}
    </Stack>
  );
}
